import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
    Box,
    Typography,
    IconButton,
    TextField,
    Divider,
} from '@mui/material';
import indicatorSvg from '../../../assets/images/indicator.svg';
import {
    RiPlayLine,
    RiPauseLine,
    RiRefreshLine,
    RiArrowDownSLine,
    RiDragMoveLine,
    RiImageLine,
    RiVideoLine,
    RiText,
    RiShapeLine,
    RiRestaurantLine,
    RiFileTextLine,
    RiSkipDownLine,
} from '@remixicon/react';
import {
    setVideoCurrentTime,
    setVideoDuration,
    setVideoPlaying,
    selectElement,
    updateAnimationTiming,
    saveToHistory,
} from '../../redux/slices/canvasSlice';

/**
 * Timeline Component - Shows video timeline with element animations
 * 
 * Features:
 * - Time ruler with markers
 * - Play/pause controls
 * - Current time display
 * - Element tracks with animation bars
 * - Drag to adjust animation timing
 */
const Timeline = () => {
    const dispatch = useDispatch();
    const { elements, selectedElementId, videoTime, designMode } = useSelector((state) => state.canvas);
    const [isDraggingTime, setIsDraggingTime] = useState(false);
    const [isDraggingAnimation, setIsDraggingAnimation] = useState(null);
    const [isCollapsed, setIsCollapsed] = useState(false);
    const [isEditingDuration, setIsEditingDuration] = useState(false);
    const [durationInput, setDurationInput] = useState(videoTime.duration.toString());
    const timelineRef = useRef(null);
    const timelineContainerRef = useRef(null);
    const animationIntervalRef = useRef(null);
    const currentTimeRef = useRef(videoTime.currentTime);

    // Update ref when currentTime changes
    useEffect(() => {
        currentTimeRef.current = videoTime.currentTime;
    }, [videoTime.currentTime]);

    // Calculate pixels per second for timeline scaling (use container width)
    const pixelsPerSecond = useMemo(() => {
        if (!timelineContainerRef.current) return 20; // Default fallback
        const containerWidth = timelineContainerRef.current.offsetWidth - 32; // Account for padding
        return containerWidth / videoTime.duration;
    }, [videoTime.duration]);

    // Format time as MM:SS
    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
    };

    // Format time as HH:MM:SS for longer durations
    const formatTimeLong = (seconds) => {
        const hours = Math.floor(seconds / 3600);
        const mins = Math.floor((seconds % 3600) / 60);
        const secs = Math.floor(seconds % 60);
        return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
    };

    // Handle play/pause
    const handlePlayPause = () => {
        dispatch(setVideoPlaying(!videoTime.isPlaying));
    };

    // Handle timeline click to scrub
    const handleTimelineClick = (e) => {
        if (!timelineRef.current) return;
        const rect = timelineRef.current.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const newTime = (x / pixelsPerSecond);
        dispatch(setVideoCurrentTime(Math.max(0, Math.min(newTime, videoTime.duration))));
    };

    // Handle time input change
    const handleTimeChange = (value) => {
        const time = parseFloat(value) || 0;
        dispatch(setVideoCurrentTime(Math.max(0, Math.min(time, videoTime.duration))));
    };

    // Handle duration change
    const handleDurationChange = (value) => {
        const duration = parseFloat(value) || 30;
        dispatch(saveToHistory());
        dispatch(setVideoDuration(Math.max(1, duration)));
    };

    // Update duration input when videoTime.duration changes
    useEffect(() => {
        setDurationInput(videoTime.duration.toString());
    }, [videoTime.duration]);

    // Handle duration input blur
    const handleDurationBlur = () => {
        setIsEditingDuration(false);
        const duration = parseFloat(durationInput) || 30;
        handleDurationChange(duration);
    };

    // Handle duration input key press
    const handleDurationKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleDurationBlur();
        }
    };

    // Animation playback loop
    useEffect(() => {
        if (videoTime.isPlaying) {
            animationIntervalRef.current = setInterval(() => {
                const currentTime = currentTimeRef.current;
                const newTime = currentTime + 0.1; // Update every 100ms
                if (newTime >= videoTime.duration) {
                    dispatch(setVideoPlaying(false));
                    dispatch(setVideoCurrentTime(videoTime.duration));
                } else {
                    dispatch(setVideoCurrentTime(newTime));
                }
            }, 100);
        } else {
            if (animationIntervalRef.current) {
                clearInterval(animationIntervalRef.current);
                animationIntervalRef.current = null;
            }
        }

        return () => {
            if (animationIntervalRef.current) {
                clearInterval(animationIntervalRef.current);
            }
        };
    }, [videoTime.isPlaying, videoTime.duration, dispatch]);

    // Get element icon based on type
    const getElementIcon = (type, properties) => {
        switch (type) {
            case 'text':
                return <RiFileTextLine size={16} color="white" />;
            case 'image':
            case 'shape':
                return <RiShapeLine size={16} color="white" />;
            case 'menuItem':
                return <RiRestaurantLine size={16} color="white" />;
            case 'video':
                return <RiVideoLine size={16} color="white" />;
            default:
                return <Box sx={{ width: 16, height: 16, borderRadius: '50%', backgroundColor: '#666' }} />;
        }
    };
    // Get element background color based on type
    const getElementBackgroundColor = (type) => {
        switch (type) {
            case 'text':
                return '#5522D5';
            case 'image':
            case 'shape':
                return '#5EAC37';
            case 'menuItem':
                return '#E6336F';
            default:
                return '#6B22D5';
        }
    };

    // Get element name
    const getElementName = (element) => {
        if (element.type === 'text') {
            return element.properties.text?.substring(0, 20) || 'Text';
        } else if (element.type === 'menuItem') {
            return element.properties.name || 'Menu Item';
        } else if (element.type === 'shape') {
            return element.properties.shape ?
                element.properties.shape.charAt(0).toUpperCase() + element.properties.shape.slice(1) :
                'Shape';
        }
        return element.type.charAt(0).toUpperCase() + element.type.slice(1);
    };

    // Calculate animation bar position and width
    const getAnimationBarStyle = (animation, element, elementIndex) => {
        // Delay and duration are in milliseconds, convert to seconds
        const delaySeconds = (animation.delay || 0) / 1000;
        const durationSeconds = (animation.duration || 1000) / 1000;
        const startX = delaySeconds * pixelsPerSecond;
        const width = durationSeconds * pixelsPerSecond;
        const backgroundColor = getElementBackgroundColor(element.type);
        const isSelected = selectedElementId === element.id;

        return {
            position: 'absolute',
            left: `${startX}px`,
            width: `${Math.max(40, width)}px`, // Minimum width for visibility
            height: '32px',
            backgroundColor: backgroundColor,
            borderRadius: '4px',
            display: 'flex',
            alignItems: 'center',
            padding: '0 8px',
            cursor: 'pointer',
            border: isSelected ? '2px solid #000' : 'none',
            boxShadow: isSelected ? '0 2px 4px rgba(0,0,0,0.2)' : 'none',
        };
    };

    // Generate time markers
    const timeMarkers = useMemo(() => {
        const markers = [];
        const interval = videoTime.duration <= 60 ? 10 : videoTime.duration <= 300 ? 30 : 60; // 10s, 30s, or 60s intervals
        for (let i = 0; i <= videoTime.duration; i += interval) {
            markers.push(i);
        }
        return markers;
    }, [videoTime.duration]);

    // All elements for timeline display (show all, even without animations)
    const timelineElements = useMemo(() => {
        return [...elements].sort((a, b) => b.zIndex - a.zIndex); // Sort by zIndex (top to bottom)
    }, [elements]);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', backgroundColor: 'white', height: '100%', maxHeight: '300px', overflow: 'hidden' }}>
            {/* Header Controls */}
            <Box
                sx={{
                    padding: '12px 16px',
                    borderBottom: isCollapsed ? 'none' : '1px solid #E0E0E0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 2,
                    position: 'relative',
                    flexShrink: 0, // Prevent header from shrinking
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start', gap: 2 }}>
                    <IconButton
                        size="small"
                        onClick={() => {
                            dispatch(setVideoCurrentTime(0));
                            dispatch(setVideoPlaying(false));
                        }}
                        sx={{
                            color: '#666',
                            backgroundColor: 'transparent',
                            '&:hover': { backgroundColor: '#F5F5F5' },
                        }}
                    >
                        <RiRefreshLine size={20} />
                    </IconButton>

                </Box>
                {/* Refresh/Reset */}

                {/* Play/Pause */}
                <IconButton
                    size="small"
                    onClick={handlePlayPause}
                    sx={{
                        backgroundColor: '#000000',
                        color: 'white',
                        padding: '6px',
                        '&:hover': { backgroundColor: '#333333' },
                    }}
                >
                    {videoTime.isPlaying ? <RiPauseLine size={18} /> : <RiPlayLine size={18} />}
                </IconButton>

                {/* Current Time */}
                <Typography variant="h6" fontWeight={600} sx={{ minWidth: '90px', fontSize: '16px' }}>
                    {formatTimeLong(videoTime.currentTime)}
                </Typography>

                {/* Divider */}
                <Divider orientation="vertical" flexItem sx={{ height: '24px', margin: '0 8px' }} />

                {/* Total Duration - Editable */}
                {isEditingDuration ? (
                    <TextField
                        size="small"
                        type="number"
                        value={durationInput}
                        onChange={(e) => setDurationInput(e.target.value)}
                        onBlur={handleDurationBlur}
                        onKeyPress={handleDurationKeyPress}
                        autoFocus
                        inputProps={{
                            min: 1,
                            step: 1,
                            style: { textAlign: 'center', width: '70px', padding: '4px' }
                        }}
                        sx={{
                            backgroundColor: '#F5F5F5',
                            borderRadius: '4px',
                            '& .MuiOutlinedInput-root': {
                                backgroundColor: '#F5F5F5',
                                borderRadius: '4px',
                                padding: '4px 12px',
                                '& fieldset': { border: 'none' },
                            },
                        }}
                    />
                ) : (
                    <Box
                        onClick={() => setIsEditingDuration(true)}
                        sx={{
                            backgroundColor: '#F5F5F5',
                            borderRadius: '4px',
                            padding: '4px 12px',
                            minWidth: '80px',
                            textAlign: 'center',
                            cursor: 'pointer',
                            '&:hover': {
                                backgroundColor: '#E0E0E0',
                            },
                        }}
                    >
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '14px' }}>
                            {formatTimeLong(videoTime.duration)}
                        </Typography>
                    </Box>
                )}

                {/* Collapse Button */}
                <IconButton
                    size="small"
                    onClick={() => setIsCollapsed(!isCollapsed)}
                    sx={{
                        color: '#666',
                        position: 'absolute',
                        right: '16px',
                        '&:hover': { backgroundColor: '#F5F5F5' },
                    }}
                >
                    <RiSkipDownLine
                        size={20}
                        style={{
                            transform: isCollapsed ? 'rotate(180deg)' : 'rotate(0deg)',
                            transition: 'transform 0.2s',
                        }}
                    />
                </IconButton>
            </Box>

            {/* Timeline Ruler */}
            {!isCollapsed && (
                <Box
                    sx={{
                        padding: '8px 16px',
                        borderBottom: '1px solid #E0E0E0',
                        backgroundColor: '#FAFAFA',
                        position: 'relative',
                        flexShrink: 0,
                    }}
                    ref={timelineContainerRef}
                >
                    <Box
                        ref={timelineRef}
                        onClick={handleTimelineClick}
                        sx={{
                            position: 'relative',
                            height: '40px',
                            width: '100%',
                            cursor: 'pointer',
                        }}
                    >
                        {/* Time Markers */}
                        {timeMarkers.map((time) => (
                            <Box
                                key={time}
                                sx={{
                                    position: 'absolute',
                                    left: `${(time / videoTime.duration) * 100}%`,
                                    top: 0,
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                }}
                            >
                                <Box
                                    sx={{
                                        width: '1px',
                                        height: '20px',
                                        backgroundColor: '#CCCCCC',
                                    }}
                                />
                                <Typography
                                    variant="caption"
                                    sx={{
                                        fontSize: '10px',
                                        color: '#666',
                                        marginTop: '4px',
                                    }}
                                >
                                    {formatTime(time)}
                                </Typography>
                            </Box>
                        ))}

                        {/* Current Time Indicator - Draggable */}
                        <Box
                            sx={{
                                position: 'absolute',
                                left: `${(videoTime.currentTime / videoTime.duration) * 100}%`,
                                top: 0,
                                height: '100%',
                                width: '18px',
                                zIndex: 10,
                                cursor: 'grab',
                                display: 'flex',
                                alignItems: 'flex-start',
                                justifyContent: 'center',
                                '&:active': {
                                    cursor: 'grabbing',
                                },
                            }}
                            onMouseDown={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setIsDraggingTime(true);

                                const handleMouseMove = (moveEvent) => {
                                    if (!timelineRef.current) return;
                                    const rect = timelineRef.current.getBoundingClientRect();
                                    const x = moveEvent.clientX - rect.left;
                                    const newTime = Math.max(0, Math.min((x / pixelsPerSecond), videoTime.duration));
                                    dispatch(setVideoCurrentTime(newTime));
                                };

                                const handleMouseUp = () => {
                                    setIsDraggingTime(false);
                                    document.removeEventListener('mousemove', handleMouseMove);
                                    document.removeEventListener('mouseup', handleMouseUp);
                                };

                                document.addEventListener('mousemove', handleMouseMove);
                                document.addEventListener('mouseup', handleMouseUp);
                            }}
                        >
                            <Box
                                component="img"
                                src={indicatorSvg}
                                alt="Time indicator"
                                sx={{
                                    width: '18px',
                                    height: 'auto',
                                    pointerEvents: 'none',
                                }}
                            />
                        </Box>
                    </Box>
                </Box>
            )}

            {/* Element Tracks */}
            {!isCollapsed && (
                <Box
                    sx={{
                        flex: 1,
                        minHeight: 0, // Allow flex shrinking
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        backgroundColor: '#F5F5F5',
                        padding: '8px 16px',
                        maxHeight: 'calc(400px - 120px)', // Subtract header and ruler height
                    }}
                >
                    {timelineElements.length === 0 ? (
                        <Box sx={{ padding: 4, textAlign: 'center' }}>
                            <Typography variant="body2" color="text.secondary">
                                No elements yet. Add elements from the left toolbar.
                            </Typography>
                        </Box>
                    ) : (
                        <Box
                            sx={{
                                position: 'relative',
                                minHeight: `${timelineElements.length * 40}px`,
                            }}
                        >
                            {timelineElements.map((element, index) => {
                                const hasAnimations = element.animations && element.animations.length > 0;
                                const backgroundColor = getElementBackgroundColor(element.type);
                                const isSelected = selectedElementId === element.id;

                                return (
                                    <Box
                                        key={element.id}
                                        sx={{
                                            position: 'relative',
                                            height: '32px',
                                            marginBottom: '8px',
                                            display: 'flex',
                                            alignItems: 'center',
                                        }}
                                    >
                                        {/* Animation Bars or Full Layer Bar */}
                                        {hasAnimations ? (
                                            element.animations.map((animation) => {
                                                const delaySeconds = (animation.delay || 0) / 1000;
                                                const durationSeconds = (animation.duration || 1000) / 1000;
                                                const startX = delaySeconds * pixelsPerSecond;
                                                const width = durationSeconds * pixelsPerSecond;

                                                return (
                                                    <Box
                                                        key={animation.id}
                                                        sx={{
                                                            position: 'absolute',
                                                            left: `${startX}px`,
                                                            width: `${Math.max(60, width)}px`,
                                                            height: '32px',
                                                            backgroundColor: backgroundColor,
                                                            borderRadius: '4px',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            padding: '0 12px',
                                                            cursor: 'pointer',
                                                            border: isSelected ? '2px solid #000' : 'none',
                                                            boxShadow: isSelected ? '0 2px 4px rgba(0,0,0,0.2)' : 'none',
                                                            zIndex: isSelected ? 2 : 1,
                                                        }}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            dispatch(selectElement(element.id));
                                                        }}
                                                    >
                                                        <Box sx={{ marginRight: '8px', display: 'flex', alignItems: 'center' }}>
                                                            {getElementIcon(element.type, element.properties)}
                                                        </Box>
                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                color: 'white',
                                                                fontSize: '12px',
                                                                fontWeight: 500,
                                                                whiteSpace: 'nowrap',
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis',
                                                            }}
                                                        >
                                                            {getElementName(element)}
                                                        </Typography>
                                                    </Box>
                                                );
                                            })
                                        ) : (
                                            <Box
                                                sx={{
                                                    position: 'absolute',
                                                    left: 0,
                                                    width: '100%',
                                                    height: '32px',
                                                    backgroundColor: backgroundColor,
                                                    borderRadius: '4px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    padding: '0 12px',
                                                    cursor: 'pointer',
                                                    border: isSelected ? '2px solid #000' : 'none',
                                                    boxShadow: isSelected ? '0 2px 4px rgba(0,0,0,0.2)' : 'none',
                                                }}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    dispatch(selectElement(element.id));
                                                }}
                                            >
                                                <Box sx={{ marginRight: '8px', display: 'flex', alignItems: 'center' }}>
                                                    {getElementIcon(element.type, element.properties)}
                                                </Box>
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        color: 'white',
                                                        fontSize: '12px',
                                                        fontWeight: 500,
                                                        whiteSpace: 'nowrap',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                    }}
                                                >
                                                    {getElementName(element)}
                                                </Typography>
                                            </Box>
                                        )}
                                    </Box>
                                );
                            })}
                        </Box>
                    )}
                </Box>
            )}
        </Box>
    );
};

export default Timeline;

